import React, { useState } from 'react';
import { CalendarPlus, RotateCcw, Loader2, Undo2 } from 'lucide-react';
import client from '../../api/client';

const plus7 = () => { const d = new Date(); d.setDate(d.getDate() + 7); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

// Personal reply-by date for late invitees + "Reopen RSVP" for auto-declined guests.
export const LateInviteControls = ({ guest, onChanged, toast }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(guest.reply_by_override || plus7());
  const [busy, setBusy] = useState(false);
  const reopen = !!guest.auto_declined_at;
  const canLate = !reopen && !['Yes', 'No'].includes(guest.rsvp);
  if (!reopen && !canLate) return null;

  const undo = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/guests/${guest.id}/undo-auto-decline`);
      onChanged({ rsvp: 'Pending', auto_declined_at: null });
      toast?.({ title: 'Auto-decline undone', description: data.message_cancelled ? 'Their auto-decline message was cancelled before it went out.' : data.message_already_sent ? 'Note: their auto-decline message had already been sent.' : `${guest.name} is back to Pending.` });
    } catch (e) {
      toast?.({ title: 'Could not undo', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/guests/${guest.id}/${reopen ? 'reopen-rsvp' : 'late-invite'}`, { replyBy: date });
      onChanged(reopen
        ? { rsvp: 'Pending', auto_declined_at: null, reply_by_override: data.reply_by_override }
        : { reply_by_override: data.reply_by_override });
      toast?.({ title: reopen ? 'RSVP reopened' : 'Late invite set', description: `${guest.name} can reply until ${fmt(data.reply_by_override)}. Send their invitation from the Invitations tab.` });
      setOpen(false);
    } catch (e) {
      toast?.({ title: 'Could not save', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      {guest.reply_by_override && !reopen && (
        <span data-testid={`late-badge-${guest.id}`} className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBF1EA] text-[#A15A2A]">Personal reply-by {fmt(guest.reply_by_override)}</span>
      )}
      {reopen && !open && (
        <button data-testid={`undo-auto-decline-${guest.id}`} disabled={busy} onClick={undo} className="text-xs text-[#7B2C3B] hover:underline inline-flex items-center gap-1 disabled:opacity-50">
          <Undo2 className="w-3 h-3" /> Undo auto-decline
        </button>
      )}
      {!open ? (
        <button data-testid={`${reopen ? 'reopen-rsvp' : 'late-invite'}-${guest.id}`} onClick={() => setOpen(true)}
                className="text-xs text-[#7B2C3B] hover:underline inline-flex items-center gap-1">
          {reopen ? <><RotateCcw className="w-3 h-3" /> Reopen RSVP</> : <><CalendarPlus className="w-3 h-3" /> {guest.reply_by_override ? 'Change personal reply-by' : 'Late invite'}</>}
        </button>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-xs">
          <span className="text-[#6A6A6A]">Reply by</span>
          <input data-testid={`late-date-${guest.id}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="px-2 py-1 rounded border border-[#EFE3D8] bg-white text-xs" />
          <button data-testid={`late-save-${guest.id}`} disabled={busy || !date} onClick={submit} className="px-3 py-1 rounded-full bg-[#7B2C3B] text-white disabled:opacity-50 inline-flex items-center gap-1">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}{reopen ? 'Reopen' : 'Save'}
          </button>
          {guest.reply_by_override && !reopen && (
            <button data-testid={`late-clear-${guest.id}`} onClick={async () => { await client.delete(`/guests/${guest.id}/late-invite`); onChanged({ reply_by_override: null }); setOpen(false); }} className="text-[#A15A2A] hover:underline">Remove personal date</button>
          )}
          <button data-testid={`late-cancel-${guest.id}`} onClick={() => setOpen(false)} className="text-[#9A8A7E] hover:underline">Cancel</button>
        </span>
      )}
    </span>
  );
};

export default LateInviteControls;
